import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface Post {
  id: number;
  date: string;
  type: 'announcement' | 'product';
  title: string;
  content?: string;
}

const WEEK_DAYS = ['일', '월', '화', '수', '목', '금', '토'];

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<Post[]>([]);
  const [announcements, setAnnouncements] = useState<Post[]>([]);
  const [dateCounts, setDateCounts] = useState<{ [key: string]: number }>({});
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Post[]>([]);

  const fetchPosts = async () => {
    try {
      const data: Post[] = [
        {
          id: 1,
          date: '2024-10-01',
          type: 'announcement',
          title: '공지사항 1',
          content: '공지사항 내용 1',
        },
        {
          id: 2,
          date: '2024-10-01',
          type: 'product',
          title: '상품 등록 1',
        },
        {
          id: 3,
          date: '2024-10-05',
          type: 'announcement',
          title: '공지사항 2',
          content: '공지사항 내용 2',
        },
        {
          id: 4,
          date: '2024-10-10',
          type: 'product',
          title: '상품 등록 2',
        },
      ];

      setPosts(data);

      const announcements = data.filter((post) => post.type === 'announcement');
      setAnnouncements(announcements);
    } catch (error) {
      console.error('포스트를 불러오는 데 실패했습니다:', error);
      toast.error(t('setting:homepage.fetch_error'));
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    const counts: { [key: string]: number } = {};
    posts.forEach((post) => {
      if (post.type === 'product') {
        if (counts[post.date]) {
          counts[post.date] += 1;
        } else {
          counts[post.date] = 1;
        }
      }
    });
    setDateCounts(counts);
  }, [posts]);

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    const dateStr = date.toISOString().slice(0, 10);
    const filteredProducts = posts.filter(
      (post) => post.date === dateStr && post.type === 'product'
    );
    setSelectedProducts(filteredProducts);
  };

  const renderCalendarCells = () => {
    const calendarDates = generateCalendar(currentMonth);
    return calendarDates.map((date, index) => {
      const dateStr = date.toISOString().slice(0, 10);
      const count = dateCounts[dateStr] || 0;
      const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
      const isSelected =
        selectedDate &&
        date.getFullYear() === selectedDate.getFullYear() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getDate() === selectedDate.getDate();

      const maxCount = 5;
      const intensity = Math.min(count, maxCount) / maxCount;
      const bgColor = intensity > 0 ? `rgba(34, 197, 94, ${intensity})` : 'transparent';

      return (
        <div
          key={index}
          className={`flex flex-col items-center justify-center border h-12 cursor-pointer ${
            isCurrentMonth ? 'text-black' : 'text-gray-400'
          } ${isSelected ? 'border-2 border-[#34d399]' : ''}`}
          onClick={() => isCurrentMonth && handleDateClick(date)}
          style={{ backgroundColor: isCurrentMonth ? 'white' : '#f0f0f0' }}
        >
          <span className="text-sm">{date.getDate()}</span>
          {count > 0 && (
            <span
              className="w-2 h-2 rounded-full mt-1"
              style={{
                backgroundColor: bgColor,
              }}
            ></span>
          )}
        </div>
      );
    });
  };

  const generateCalendar = (date: Date): Date[] => {
    const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const startDay = startOfMonth.getDay();
    const totalDays = endOfMonth.getDate();
    const dates: Date[] = [];

    // 이전 달 날짜 추가
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = new Date(date.getFullYear(), date.getMonth(), -i);
      dates.push(prevDate);
    }

    // 현재 달 날짜 추가
    for (let i = 1; i <= totalDays; i++) {
      dates.push(new Date(date.getFullYear(), date.getMonth(), i));
    }

    // 다음 달 날짜 추가
    while (dates.length % 7 !== 0) {
      const nextDate = new Date(date.getFullYear(), date.getMonth() + 1, dates.length - totalDays - startDay + 1);
      dates.push(nextDate);
    }

    return dates;
  };

  return (
    <div className="p-8">
      <h2 className="text-xl font-bold mb-4">{t('setting:homepage.title')}</h2>
      <div className="flex flex-col md:flex-row gap-8">
        <div className="md:w-1/2">
          <h3 className="text-lg font-semibold mb-2">{t('setting:homepage.announcements')}</h3>
          {announcements.length > 0 ? (
            <ul className="space-y-2">
              {announcements.map((announcement) => (
                <li
                  key={announcement.id}
                  className="p-4 border rounded-md shadow-sm bg-gray-50"
                >
                  <p className="font-medium">{announcement.title}</p>
                  <p className="text-sm text-gray-500">{announcement.content}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p>{t('setting:homepage.no_announcements')}</p>
          )}
        </div>

        <div className="md:w-1/2">
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={prevMonth}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              &lt;
            </button>
            <h3 className="text-lg font-semibold">
              {currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월
            </h3>
            <button
              onClick={nextMonth}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              &gt;
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {WEEK_DAYS.map((day, index) => (
              <div key={index} className="text-center font-medium text-sm">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {renderCalendarCells()}
          </div>

          {selectedDate && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-2">
                {t('setting:homepage.posts_on', { date: selectedDate.toLocaleDateString() })}
              </h3>
              {selectedProducts.length > 0 ? (
                <ul className="space-y-2">
                  {selectedProducts.map((product) => (
                    <li
                      key={product.id}
                      className="p-4 border rounded-md shadow-sm bg-gray-50"
                    >
                      <p className="font-medium">{product.title}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>{t('setting:homepage.no_products')}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomePage;
